import React from 'react';
import styled from 'styled-components';
import t from 'modules/i18n/intl';
import { colors } from '@atlaskit/theme';
import { Dimensions, Font } from 'components/visuals';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { MACTableType } from '../table/TableContent';
import EditFilledIcon from '@atlaskit/icon/glyph/edit-filled';
import { useDispatch } from 'react-redux';
import { openPONumberUpdateModal } from '../duck/actions/orders';
import { IconButton } from '@atlaskit/button/new';
import { messages } from '../messages';
import { removeTimezone } from '../common/utils';

const ExpandedRowWrapper = styled.td`
  border-bottom: 2px solid ${colors.N900}24;
  border-radius: 3px;
  padding: 18px 0;
  box-sizing: border-box;
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const ExpandedRowSection = styled(FlexColumn)`
  flex-grow: 1;
  max-width: ${Dimensions.units(65)}px;
  gap: ${Dimensions.units(2)}px;
`;

const ExpandedRowCell = styled(FlexRow)`
  gap: ${Dimensions.units(2)}px;
`;

const ExpandedCellTitle = styled.span`
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: ${Dimensions.units(2.5)}px;
  color: ${colors.DN200};
  width: ${Dimensions.units(15)}px;
  text-align: right;
  font-family: ${Font.displayFamily};
`;

const CartItemsSection = styled(FlexColumn)`
  width: ${Dimensions.units(45)}px;
`;

const DateCell = ({ date, tableType }: { date: string; tableType: MACTableType }) => (
  <ExpandedRowCell data-cy="mac-orders-expiry-date">
    <ExpandedCellTitle>
      {tableType === MACTableType.orders
        ? t('mac.orders.table.table-item.expanded.expiry-date')
        : t('mac.invoices.table.table-item.expanded.paid-on')}
    </ExpandedCellTitle>
    <FormattedDate value={removeTimezone(date)} day="numeric" month="short" year="numeric" />
  </ExpandedRowCell>
);

const EditIconComponent = () => (
  <EditFilledIcon label="mac.orders.table.table-item.action.edit" size="small" />
);

interface Props {
  rowItem: any;
  tableType: MACTableType;
  senNumber?: string;
}

const OrdersExpandedRow: React.FC<Props> = ({ rowItem, tableType }) => {
  const dispatch = useDispatch();

  const {
    cartOrderDetails: {
      items,
      expiry,
      purchaseOrderNumber,
      technicalContactFirstName,
      technicalContactLastName,
      technicalContactEmail,
      technicalContactOrganisationName,
      billingContactFirstName,
      billingContactLastName,
      billingContactEmail,
      billingContactOrganisationName,
    },
    orderId,
  } = rowItem;

  const cartItems = items
    .map((item) => (item.proratedUsdPrice > 0 ? item.description : null))
    .filter((i) => i !== null);

  return (
    <ExpandedRowWrapper key={orderId} colSpan={5}>
      <FlexRow>
        <ExpandedRowSection>
          {expiry && <DateCell date={expiry} tableType={tableType} />}
          <ExpandedRowCell data-cy="mac-orders-po-number">
            <ExpandedCellTitle>
              {t('mac.orders.table.table-item.expanded.po-number')}
            </ExpandedCellTitle>
            {purchaseOrderNumber || <FormattedMessage {...messages.orderPoNone} />}
            {tableType === MACTableType.orders && (
              <IconButton
                onClick={() => dispatch(openPONumberUpdateModal(rowItem))}
                appearance="subtle"
                spacing="compact"
                label="Update PO Number"
                icon={EditIconComponent}
                testId={`orders-table-update-po-number-icon-${orderId}`}
              />
            )}
          </ExpandedRowCell>
          <ExpandedRowCell data-cy="mac-orders-items">
            <ExpandedCellTitle>{t('mac.orders.table.table-item.expanded.items')}</ExpandedCellTitle>
            <CartItemsSection>{cartItems.map((item) => item)}</CartItemsSection>
          </ExpandedRowCell>
        </ExpandedRowSection>
        <ExpandedRowSection>
          <ExpandedRowCell data-cy="mac-orders-technical-contact">
            <ExpandedCellTitle>
              {t('mac.orders.table.table-item.expanded.technical-contact')}
            </ExpandedCellTitle>
            <FlexRow>
              {technicalContactFirstName} {technicalContactLastName} ({technicalContactEmail})
              <br />
              {technicalContactOrganisationName}
            </FlexRow>
          </ExpandedRowCell>
          <ExpandedRowCell data-cy="mac-orders-billing-contact">
            <ExpandedCellTitle>
              {t('mac.orders.table.table-item.expanded.billing-contact')}
            </ExpandedCellTitle>
            <FlexRow>
              {billingContactFirstName} {billingContactLastName} ({billingContactEmail})
              <br />
              {billingContactOrganisationName}
            </FlexRow>
          </ExpandedRowCell>
        </ExpandedRowSection>
      </FlexRow>
    </ExpandedRowWrapper>
  );
};

export default OrdersExpandedRow;
